import {
  AIIconPicker,
  AITextAreaInput,
  Button,
  Dialog,
  RoleVisibilitySelect,
  TextInput,
  type IconoirIconName,
} from "@vellum/ui";
import { suggestIconNames, vellumApiSdk } from "@vellum/micro-app-auth";
import { LinkSection, type HubFolderItem } from "@vellum/vellum-api";
import { useFormik } from "formik";
import * as yup from "yup";

import { SectionPicker } from "@/components/SectionPicker";

export interface FolderFormValues {
  title: string;
  description: string;
  section: LinkSection;
  icon: string | null;
  visible_to_roles: string[];
}

const validationSchema = yup.object({
  title: yup.string().trim().required("Title is required"),
  description: yup.string().trim(),
  section: yup
    .string()
    .oneOf(Object.values(LinkSection))
    .required("Section is required"),
});

interface FolderFormDialogProps {
  isOpen: boolean;
  /** The folder being edited, or null when creating a new one. */
  folder: HubFolderItem | null;
  /** Section the "Add folder" card belongs to; seeds a new folder's section. */
  defaultSection: LinkSection;
  onClose: () => void;
  onSubmit: (values: FolderFormValues) => Promise<void>;
}

export function FolderFormDialog({
  isOpen,
  folder,
  defaultSection,
  onClose,
  onSubmit,
}: FolderFormDialogProps) {
  const formik = useFormik<FolderFormValues>({
    enableReinitialize: true,
    initialValues: {
      title: folder?.title ?? "",
      description: folder?.description ?? "",
      section: folder?.section ?? defaultSection,
      icon: folder?.icon ?? null,
      visible_to_roles: folder?.visible_to_roles ?? [],
    },
    validationSchema,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const titleError = formik.touched.title ? formik.errors.title : undefined;
  const sectionChanged =
    folder !== null && formik.values.section !== folder.section;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={folder ? "Edit folder" : "Add folder"}
    >
      <form onSubmit={formik.handleSubmit} className="mt-4 flex flex-col gap-4">
        <TextInput
          id="title"
          name="title"
          label="Title"
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          color={titleError ? "error" : undefined}
          errortext={titleError}
        />
        <AITextAreaInput
          id="description"
          name="description"
          label="Description"
          rows={2}
          className="resize-y"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          onSuggest={async (currentValue) => {
            const result = await vellumApiSdk.suggestDescription({
              body: {
                subject:
                  formik.values.title.trim() ||
                  "a folder of internal hub links",
                context: null,
                current_text: currentValue.trim() || null,
              },
            });
            if (result.data === undefined) {
              throw new Error("Suggestion request failed");
            }
            return result.data.suggestion;
          }}
          onSuggestion={(suggestion) =>
            void formik.setFieldValue("description", suggestion)
          }
        />
        <div>
          <SectionPicker
            value={formik.values.section}
            onChange={(section) =>
              void formik.setFieldValue("section", section)
            }
          />
          {sectionChanged ? (
            <p className="mt-1.5 text-xs text-gray-500">
              The folder&apos;s links move to the {formik.values.section}{" "}
              section with it.
            </p>
          ) : null}
        </div>
        <AIIconPicker
          label="Icon"
          value={formik.values.icon}
          onChange={(icon: IconoirIconName | null) =>
            void formik.setFieldValue("icon", icon)
          }
          subject={formik.values.title}
          context={formik.values.description}
          onSuggest={suggestIconNames}
        />
        <RoleVisibilitySelect
          itemLabel="folder"
          value={formik.values.visible_to_roles}
          onChange={(roles) =>
            void formik.setFieldValue("visible_to_roles", roles)
          }
        />
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={formik.isSubmitting}>
            {folder ? "Save changes" : "Add folder"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
